/*
 * The help-desk ASSISTED shell (DESIGN-HELPDESK-KNOWN-TICKET-ASSISTED-
 * COMPLETION-V1 §2.3/§2.4; LOCKED contract HELPDESK-CONTRACT-LOCKED-1102).
 * Rendered by App instead of the normal app shell while an assisted session
 * is active (auth/authSource.ts). It mounts the REAL Get Ready screens — the
 * card list, the profile wizard, the waiver flow, heat select and the
 * questionnaire — for the session's ONE ticket:
 *   - every my-api call carries `Authorization: Assist <as1>` (authSource);
 *   - no tickets-service, settings-direct or Cognito call is made;
 *   - storage is memory-only for the page's life;
 *   - there is NO wallet, orders, profile hub or navigation: those routes do
 *     not exist here (any other path lands on /get-ready).
 * Completion: my-api's hook settles the session when readiness reaches
 * self_service_complete; this shell watches GET /assist/status (on mount, on
 * every readiness refresh and every 15 s) and then leaves through the return
 * URL (checkinclient://helpdesk/return…), else shows the done screen. "Return
 * to the help desk" does the same at any time (status incomplete).
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { assistEnded, assistedSession, endAssistedMode, subscribeAssisted } from '../auth/authSource'
import { Brand } from '../components/ui'
import { assistReturn, fetchAssistStatus } from '../data/assist'
import { subscribeReadinessRefresh } from '../data/getReady'
import { myApiBase } from '../data/myApi'
import { GetReady, HeatEntry, QuestionnaireEntry } from './GetReady'
import { WaiverFlow } from './waiver/WaiverFlow'
import { ProfileWizard } from './wizard/ProfileWizard'

export const ASSIST_STATUS_POLL_MS = 15_000

export function AssistBanner() {
  return (
    <div role="status" data-testid="assist-banner" style={{ background: '#fff4d6', border: '1px solid #e6c56a', borderRadius: 8, padding: '10px 12px', margin: '12px 0', fontWeight: 600 }}>
      Assisted at the help desk
    </div>
  )
}

export function AssistDone({ complete }: { complete: boolean }) {
  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: 16 }} data-testid="assist-done">
      <div style={{ marginBottom: 14 }}><Brand /></div>
      <AssistBanner />
      <p data-testid="assist-returned">{complete ? 'All done. ' : ''}Please return the tablet to the help desk.</p>
    </div>
  )
}

export interface AssistShellProps {
  fetchImpl?: typeof fetch
  /** Leaves the page for the app's return deep link. */
  navigateExternal?: (url: string) => void
  base?: string | null
}

export function AssistShell({ fetchImpl = fetch, navigateExternal = (u) => window.location.assign(u), base = myApiBase() }: AssistShellProps) {
  const session = useSyncExternalStore(subscribeAssisted, assistedSession)
  const ended = useSyncExternalStore(subscribeAssisted, assistEnded)
  const finishing = useRef(false)

  const finish = useCallback(
    async (status: 'incomplete' | 'cancelled') => {
      if (finishing.current) return
      finishing.current = true
      const s = assistedSession()
      let complete = false
      let url: string | null = null
      if (s && base) {
        try {
          const r = await assistReturn(base, s, status, fetchImpl)
          complete = r.status === 'complete'
          url = r.url
        } catch {
          // the session ends locally either way
        }
      }
      // Drop the as1 session from memory BEFORE leaving.
      endAssistedMode(complete)
      if (url) navigateExternal(url)
    },
    [base, fetchImpl, navigateExternal],
  )

  useEffect(() => {
    if (!session || !base) return
    let stopped = false
    const check = async () => {
      try {
        const outcome = await fetchAssistStatus(base, session, fetchImpl)
        if (!stopped && outcome === 'complete') void finish('incomplete')
      } catch {
        // a failed status read changes nothing; the next tick retries
      }
    }
    void check()
    const unsubscribe = subscribeReadinessRefresh(() => void check())
    const timer = setInterval(() => void check(), ASSIST_STATUS_POLL_MS)
    return () => {
      stopped = true
      unsubscribe()
      clearInterval(timer)
    }
  }, [session, base, fetchImpl, finish])

  if (ended || !session) return <AssistDone complete={ended?.complete ?? false} />

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 16px 24px' }} data-testid="assist-shell">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12 }}>
        <Brand />
        <button type="button" onClick={() => void finish('incomplete')} style={{ padding: '8px 12px', borderRadius: 8 }}>
          Return to the help desk
        </button>
      </div>
      <AssistBanner />
      <Routes>
        <Route path="/get-ready" element={<GetReady />} />
        <Route path="/get-ready/profile" element={<Navigate to="/get-ready/profile/step-1" replace />} />
        <Route path="/get-ready/profile/:stepSlug" element={<ProfileWizard />} />
        <Route path="/get-ready/emergency" element={<Navigate to="/get-ready/profile/step-4" replace />} />
        <Route path="/get-ready/waiver/:eventId" element={<Navigate to="doc-1" replace />} />
        <Route path="/get-ready/waiver/:eventId/:stepSlug" element={<WaiverFlow />} />
        <Route path="/get-ready/heat/:eventId" element={<HeatEntry />} />
        <Route path="/get-ready/questionnaire/:eventId" element={<QuestionnaireEntry />} />
        <Route path="*" element={<Navigate to="/get-ready" replace />} />
      </Routes>
    </div>
  )
}
